import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_CURRENCIES, ALL_PAYMENT_METHODS } from '../../constants/appConstants';
import { parseInitialPaymentAmount, validatePaymentAmount } from '../../utils/paymentDialog';
import { formatCurrency } from '../../utils/currency';
import { useFeedback } from '../../context/FeedbackContext';
import SelectField from '../inputs/SelectField';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function} props.onSubmit — async ({ amount, note, currency }) => Promise
 * @param {string} [props.initialCurrency]
 * @param {string} [props.defaultCurrency] — pre-selected when opening without initialCurrency
 * @param {string} [props.title]
 * @param {number|string|null} [props.initialAmount]
 * @param {string} [props.initialNote='']
 * @param {string} [props.submitLabel]
 * @param {number|null} [props.maxAmount] — amount due cap in check currency
 * @param {string} [props.checkCurrency]
 * @param {Array} [props.exchangeRates]
 * @param {boolean} [props.requirePaymentMethod=false] — required when closing a check
 */
function AddPaymentDialog({
  open,
  onClose,
  onSubmit,
  title,
  initialAmount,
  initialNote = '',
  initialCurrency,
  defaultCurrency,
  submitLabel: submitLabelProp,
  maxAmount,
  checkCurrency,
  exchangeRates,
  isMobile,
  requirePaymentMethod = false,
}) {
  const { t } = useTranslation();
  const { showError } = useFeedback();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);

  const paymentMethodOptions = useMemo(
    () => ALL_PAYMENT_METHODS.map((value) => ({
      value,
      label: t(`paymentMethods.${value}`),
    })),
    [t],
  );

  useEffect(() => {
    if (open) {
      setAmount(parseInitialPaymentAmount(initialAmount));
      setNote(initialNote || '');
      setPaymentCurrency(initialCurrency || defaultCurrency || SUPPORTED_CURRENCIES[0].code);
      setPaymentMethod('');
      setLoading(false);
    }
  }, [open, initialAmount, initialNote, initialCurrency, defaultCurrency]);

  const dialogTitle = title || t('cashiering.addPayment');
  const amountLabel = t('cashiering.amount');
  const currencyLabel = t('cashiering.paymentCurrency');
  const noteLabel = t('cashiering.noteOptional');
  const cancelLabel = t('cashiering.cancel');
  const submitLabel = submitLabelProp || t('cashiering.addPayment');
  const savingLabel = t('cashiering.saving');

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleSubmit = async () => {
    const result = validatePaymentAmount(amount, {
      maxAmount,
      paymentCurrency,
      checkCurrency,
      exchangeRates,
    });
    if (!result.valid) {
      if (result.exceedsMax) {
        showError(t('payments.amountExceedsDue', {
          max: formatCurrency(result.maxAmount, checkCurrency),
        }));
      } else if (result.missingRate) {
        showError(t('checks.exchangeRateMissingBody'));
      } else {
        showError(t('cashiering.invalidAmount'));
      }
      return;
    }
    if (requirePaymentMethod && !paymentMethod) {
      showError(t('payments.paymentMethodRequired'));
      return;
    }
    setLoading(true);
    try {
      await onSubmit({
        amount: result.amount,
        note: note || undefined,
        currency: paymentCurrency || undefined,
        paymentMethod: paymentMethod || undefined,
      });
      setAmount('');
      setNote('');
      onClose();
    } catch (error) {
      showError(error.response?.data?.error || t('cashiering.addPaymentFailed'));
    } finally {
      setLoading(false);
    }
  };

  const validation = useMemo(() => validatePaymentAmount(amount, {
    maxAmount,
    paymentCurrency,
    checkCurrency,
    exchangeRates,
  }), [amount, maxAmount, paymentCurrency, checkCurrency, exchangeRates]);

  const exceedsHelper = validation.exceedsMax && checkCurrency
    ? t('payments.amountExceedsDue', { max: formatCurrency(validation.maxAmount, checkCurrency) })
    : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={amountLabel}
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          margin="normal"
          required
          inputProps={{ step: 0.01, min: 0 }}
          error={validation.exceedsMax}
          helperText={exceedsHelper || undefined}
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="payment-currency-label">{currencyLabel}</InputLabel>
          <Select
            labelId="payment-currency-label"
            label={currencyLabel}
            value={paymentCurrency}
            onChange={(e) => setPaymentCurrency(e.target.value)}
          >
            {SUPPORTED_CURRENCIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.code} ({c.symbol})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SelectField
          fullWidth
          size="small"
          label={t('payments.paymentMethod')}
          options={paymentMethodOptions}
          value={paymentMethod}
          onChange={(value) => setPaymentMethod(value || '')}
          includeEmptyOption={!requirePaymentMethod}
          emptyLabel={
            requirePaymentMethod
              ? t('payments.paymentMethodRequired')
              : t('payments.paymentMethodOptional')
          }
          required={requirePaymentMethod}
          sx={{ mt: 2 }}
          data-testid="payment-method-select"
        />
        <TextField
          fullWidth
          label={noteLabel}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          margin="normal"
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>{cancelLabel}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading
            || !amount
            || !paymentCurrency
            || !validation.valid
            || (requirePaymentMethod && !paymentMethod)
          }
        >
          {loading ? savingLabel : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddPaymentDialog;
